import moment from 'moment';
import { UserProfession } from '~/enums/UserProfession';
import { Colors } from '~/themes/colors';
import { Column, Row, Text } from '~/commonComponents';
import { useMediaQuery } from '~/hooks';
import { ProfessionalNameRow } from '../shared/ProfessionalNameRow';
import AttendanceBadgeInline from '../shared/AttendanceBadgeInline';
import { IProfessionalCardUser } from '../../interfaces';

interface IProfessionalCardDetailsProps {
  user: IProfessionalCardUser;
}

export const ProfessionalCardDetails = ({ user }: IProfessionalCardDetailsProps) => {
  const { id, fullname, profession, rating, isAvailable, yearsOfExperience, badges, datetime } =
    user;
  const textColor = isAvailable ? Colors.neutral_500 : Colors.neutral_300;
  const hasBadges = badges && badges.length > 0;
  const isToday = moment(datetime).isSame(moment(), 'day');
  const [isSmallScreen] = useMediaQuery(['mlg']);

  const renderWorkedOnLine = () => {
    if (isToday) {
      return (
        <Text color={textColor}>
          Working:{' '}
          <Text bold color={isAvailable ? Colors.primary_500 : Colors.primary_200}>
            TODAY
          </Text>
        </Text>
      );
    }

    return (
      <Text color={textColor}>
        Worked on:{' '}
        <Text bold color={textColor}>
          {moment(datetime).format('ddd, MMM DD, YYYY')}
        </Text>
      </Text>
    );
  };

  return (
    <Column justifyContent="center" marginLeft={16} flex={1} minWidth={0}>
      {/* Row 1: Name + Rating + Profile */}
      <ProfessionalNameRow
        id={id}
        fullname={fullname}
        rating={rating}
        isAvailable={isAvailable}
      />

      {isSmallScreen ? (
        <>
          {/* Small screen — Row 2: Profession • Experience */}
          <Row alignItems="center" style={{ whiteSpace: 'nowrap' }}>
            <Text color={textColor}>{UserProfession.getName(profession || '')}</Text>
            {yearsOfExperience != null && (
              <Text color={textColor}>
                &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                {yearsOfExperience} yr{yearsOfExperience !== 1 ? 's' : ''} Experience
              </Text>
            )}
          </Row>

          {/* Small screen — Row 3: Attendance */}
          {hasBadges && (
            <Row alignItems="center" gap={4} style={{ whiteSpace: 'nowrap' }}>
              <Text color={textColor}>Attendance:</Text>
              <AttendanceBadgeInline badges={badges} />
            </Row>
          )}

          {/* Small screen — Row 4: Worked on */}
          <Row alignItems="center" style={{ whiteSpace: 'nowrap' }}>
            {renderWorkedOnLine()}
          </Row>
        </>
      ) : (
        <>
          {/* Big screen — Row 2: Profession • Experience • Attendance */}
          <Row alignItems="center" style={{ whiteSpace: 'nowrap' }}>
            <Text color={textColor}>{UserProfession.getName(profession || '')}</Text>
            {yearsOfExperience != null && (
              <Text color={textColor}>
                &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                {yearsOfExperience} yr{yearsOfExperience !== 1 ? 's' : ''} Experience
              </Text>
            )}
            {hasBadges && (
              <Row alignItems="center" gap={4}>
                <Text color={textColor}>&nbsp;&nbsp;&bull;&nbsp;&nbsp;Attendance:</Text>
                <AttendanceBadgeInline badges={badges} />
              </Row>
            )}
          </Row>

          {/* Big screen — Row 3: Worked on */}
          <Row alignItems="center" style={{ whiteSpace: 'nowrap' }}>
            {renderWorkedOnLine()}
          </Row>
        </>
      )}
    </Column>
  );
};
